import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Progress } from "design-system/atoms/Progress"
import { FileCheck, FileClock } from "lucide-react"
import { useCustomerImportStatusQuery } from "modules/customers/hooks/useCustomerImportStatusQuery"
import { useTranslation } from "react-i18next"
import { CampaignDetailResponseDataSchema } from "schema"
import { z } from "zod"

type Campaign = z.infer<typeof CampaignDetailResponseDataSchema>

type Props = {
  campaign: Campaign
}

export function PendingImports({ campaign }: Props) {
  const pendingImports = campaign.pendingImports ?? []

  if (pendingImports.length === 0) return null

  return (
    <div className="flex flex-col divide-y rounded-lg border bg-background">
      {pendingImports.map((pendingImport) => (
        <PendingImport
          key={pendingImport.id}
          campaignId={campaign.id}
          importId={pendingImport.id}
        />
      ))}
    </div>
  )
}

type PendingImportProps = {
  campaignId: string
  importId: string
}

function PendingImport({ campaignId, importId }: PendingImportProps) {
  const { t } = useTranslation()
  const { data } = useCustomerImportStatusQuery(importId)

  if (!data || data.status === "FAILED") return null

  const isCompleted = data.status === "COMPLETED"
  const percent =
    data.totalRows === 0 ? 0 : Math.round((data.processedCount / data.totalRows) * 100)

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 p-4">
      <div className="flex flex-1 items-center gap-3">
        {isCompleted ? (
          <FileCheck className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <FileClock className="size-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-sm font-medium">
            {isCompleted
              ? t("campaigns.pendingImport.completedTitle")
              : t("campaigns.pendingImport.runningTitle")}
          </span>
          <span className="text-sm text-muted-foreground">
            {isCompleted
              ? t("campaigns.pendingImport.completedDescription", {
                  count: data.createdCount + data.updatedCount,
                })
              : t("campaigns.pendingImport.runningDescription", {
                  processedCount: data.processedCount,
                  count: data.totalRows,
                })}
          </span>
          {!isCompleted && <Progress value={percent} className="max-w-sm" />}
        </div>
      </div>
      <Button asChild variant={isCompleted ? "default" : "outline"}>
        <Link to="/campaigns/$id/insights/launch" params={{ id: campaignId }} search={{ importId }}>
          {isCompleted ? t("campaigns.pendingImport.launch") : t("campaigns.pendingImport.view")}
        </Link>
      </Button>
    </div>
  )
}
